import { Globe, MapPin } from "lucide-react";
import Link from "next/link";

type FooterLink = { label: string; href: string; external?: boolean };
const groups: { title: string; links: FooterLink[] }[] = [
  {
    title: "Get to Know Us",
    links: [
      { label: "About MOVA", href: "/info/about-mova" },
      { label: "Careers", href: "/info/careers" },
      { label: "Press Releases", href: "/info/press-releases" },
      { label: "MOVA Science", href: "/info/mova-science" }
    ]
  },
  {
    title: "Connect with Us",
    links: [{ label: "Telegram", href: "https://t.me/movamarket", external: true }]
  },
  {
    title: "Make Money with Us",
    links: [
      { label: "Sell on MOVA", href: "/info/sell-on-mova" },
      { label: "Sell under MOVA Accelerator", href: "/info/mova-accelerator" },
      { label: "Fulfilment by MOVA", href: "/info/fulfilment-by-mova" },
      { label: "Advertise Your Products", href: "/info/advertise-your-products" },
      { label: "MOVA Pay on Merchants", href: "/info/mova-pay-merchants" }
    ]
  },
  {
    title: "Let Us Help You",
    links: [
      { label: "Your Account", href: "/info/your-account" },
      { label: "Help", href: "/info/help" }
    ]
  }
];

const lowerInformationalLinks = [
  { title: "AbeBooks", desc: "Books, art & collectibles", href: "/info/abebooks" },
  { title: "ACX", desc: "Audiobook Publishing Made Easy", href: "/info/acx" },
  { title: "Audible", desc: "Download Audio Books", href: "/info/audible" },
  { title: "IMDb", desc: "Movies, TV & Celebrities", href: "/info/imdb" },
  { title: "Shopbop", desc: "Designer Fashion Brands", href: "/info/shopbop" },
  { title: "MOVA Business", desc: "Everything For Your Business", href: "/info/mova-business" },
  { title: "MOVA Cloud", desc: "Scalable Cloud Computing Services", href: "/info/mova-cloud" }
];

export function Footer() {
  return (
    <footer className="mt-10 bg-[#30497D] text-white">
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="block w-full bg-[#455B8A] py-3 text-center text-sm font-medium hover:bg-[#526a9e]"
      >
        Back to top
      </button>

      <div className="mx-auto max-w-[1000px] px-4 py-8 sm:px-6 sm:py-10">
        <div className="grid grid-cols-2 gap-4 sm:gap-6 sm:grid-cols-4">
          {groups.map((group) => (
            <div key={group.title}>
              <h3 className="mb-3 text-sm font-bold text-white">{group.title}</h3>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-[13px] text-slate-300 hover:underline">
                        {link.label}
                      </a>
                    ) : (
                      <Link href={link.href} className="text-[13px] text-slate-300 hover:underline">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-y border-white/10">
        <div className="mx-auto flex max-w-[1000px] flex-wrap items-center justify-center gap-3 px-4 py-6 sm:gap-6 sm:px-6">
          <Link href="/" className="text-2xl font-black tracking-wider text-white transition-opacity hover:opacity-90 mr-2" aria-label="MOVA home">
            MOVA
          </Link>
          <button type="button" className="flex items-center gap-1.5 rounded border border-slate-500 px-3 py-1.5 text-xs sm:text-sm hover:border-white">
            <Globe className="h-4 w-4" />
            English
          </button>
          <button type="button" className="flex items-center gap-1.5 rounded border border-slate-500 px-3 py-1.5 text-xs sm:text-sm hover:border-white">
            <span className="font-bold">ETB</span>
            ETB &ndash; Ethiopian Birr
          </button>
          <button type="button" className="flex items-center gap-1.5 rounded border border-slate-500 px-3 py-1.5 text-xs sm:text-sm hover:border-white">
            <MapPin className="h-4 w-4" />
            Ethiopia
          </button>
        </div>
      </div>

      <div className="bg-[#253C6D]">
        <div className="mx-auto grid max-w-[1000px] grid-cols-2 gap-x-4 gap-y-6 px-4 py-8 text-center sm:grid-cols-4 sm:gap-x-8 sm:px-6 sm:py-10">
          {lowerInformationalLinks.map((item, index) => (
            <Link key={`${item.title}-${index}`} href={item.href} className="block text-[12px] leading-relaxed text-slate-400 hover:text-slate-200">
              <span className="block font-bold text-slate-200">{item.title}</span>
              {item.desc}
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-[#253C6D] pb-8 pt-4 text-center text-[12px] text-slate-400">
        <div className="mb-3 flex flex-wrap justify-center gap-x-6 gap-y-2 px-4">
          <Link href="/info/conditions-of-use" className="hover:underline">Conditions of Use &amp; Sale</Link>
          <Link href="/info/privacy-notice" className="hover:underline">Privacy Notice</Link>
          <Link href="/info/interest-based-ads" className="hover:underline">Interest-Based Ads</Link>
        </div>
        <p>&copy; {new Date().getFullYear()} MOVA Market Ethiopia.</p>
        <p className="mt-1 text-slate-500">
          MOVA Market Ethiopia — Premier e-commerce marketplace.
        </p>
      </div>
    </footer>
  );
}
